import { useLanguage } from "./i18n";
import { DIMENSION_CRITERIA } from "./evidence_guidance";
import { candidateName, type Candidate, type Dimension } from "./rank_api";

/** Slot captions; rank 1 is "best", lower slots are named, nothing is labelled "worst" (unranked candidates exist). */
const SLOT_LABELS = ["最好", "第 2 名", "第 3 名"];

interface Props {
  dimension: Dimension; candidates: Candidate[]; order: string[];
  /** Number of ordered slots (3 in v4); candidates left in the pool stay unranked. */
  rankCount: number;
  onChange: (order: string[]) => void; disabled: boolean;
  /** Admin sessions show each candidate's method name next to its letter. */
  admin?: boolean;
}
export default function DimensionRanker({ dimension, candidates, order, rankCount, onChange, disabled, admin = false }: Props) {
  const { t } = useLanguage();
  const full = order.length >= rankCount;
  // Present when the protocol names this dimension; otherwise the manifest's question/help show.
  const criteria = DIMENSION_CRITERIA[dimension.id];
  const pool = candidates.filter((item) => !order.includes(item.token));
  /** Insert `token` at slot `index`; anything pushed past the last slot returns to the pool. */
  function place(token: string, index: number) {
    const next = order.filter((item) => item !== token);
    next.splice(Math.min(index, next.length), 0, token);
    onChange(next.slice(0, rankCount));
  }
  function move(index: number, offset: number) {
    const next = [...order];
    const token = next.splice(index, 1)[0];
    if (token) next.splice(index + offset, 0, token);
    onChange(next);
  }
  return <div className={"rs-ranker" + (admin ? " rs-admin" : "")} data-dimension={dimension.id} data-rank-count={rankCount}>
    {criteria
      ? <ol className="rs-criteria">{criteria.map((item) =>
          <li key={item.title}><strong>{t(item.title)}</strong>{t(item.detail)}</li>)}</ol>
      : <><h3>{t(dimension.question)}</h3><p className="rs-dimension-help">{t(dimension.help)}</p></>}
    <p className="rs-rank-instruction">{t("点击候选依次加入，或拖入名次槽")}</p>
    <div className={"rs-rank-pool" + (full ? " full" : "")} aria-label={t("未排名产物")}>{pool.map((item) =>
      <button key={item.token} type="button" disabled={disabled} draggable={!disabled} data-add-rank={item.label}
        aria-disabled={full} title={full ? t("前三名已选满；拖入名次槽可替换，或先移出一个") : undefined}
        aria-label={t("加入排名") + " · " + t("产物") + " " + item.label}
        onDragStart={(event) => event.dataTransfer.setData("text/plain", item.token)}
        onClick={() => { if (!full) place(item.token, order.length); }}>
        <span className={"rs-badge badge-" + item.label}>{item.label}</span>
        {admin && item.method_label && <span className="rs-admin-method">{item.method_label}</span>}
        <span className="rs-pool-name">{t("产物")} {item.label}</span><span>{full ? "" : "＋"}</span>
      </button>)}
      {pool.length === 0 && <span className="rs-pool-empty">{t("所有产物均已排名")}</span>}</div>
    {full && pool.length > 0 && <p className="rs-pool-hint">{t("前三名已选满；其余产物保持未排名。拖入名次槽可替换，或点 × 移出。")}</p>}
    <div className="rs-rank-slots">{Array.from({ length: rankCount }, (_, index) => {
      const token = order[index];
      const item = candidates.find((candidate) => candidate.token === token);
      return <div className={"rs-rank-slot " + (item ? "filled" : "")} key={index} data-rank-slot={index + 1}
        onDragOver={(event) => { if (!disabled) event.preventDefault(); }}
        onDrop={(event) => {
          event.preventDefault(); const dropped = event.dataTransfer.getData("text/plain");
          if (!disabled && candidates.some((candidate) => candidate.token === dropped)) place(dropped, index);
        }}>
        <span className="rs-rank-number">{index + 1}<small>{t(SLOT_LABELS[index] ?? "第 " + (index + 1) + " 名")}</small></span>
        {item ? <div className="rs-ranked-item" draggable={!disabled}
          onDragStart={(event) => event.dataTransfer.setData("text/plain", item.token)}>
          <span className={"rs-badge badge-" + item.label}>{item.label}</span><strong>{t("产物")} {candidateName(item, admin)}</strong>
          <div className="rs-rank-actions">
            <button type="button" aria-label={t("向前移动产物 ") + item.label} disabled={disabled || index === 0} onClick={() => move(index, -1)}>↑</button>
            <button type="button" aria-label={t("向后移动产物 ") + item.label} disabled={disabled || index === order.length - 1} onClick={() => move(index, 1)}>↓</button>
            <button type="button" aria-label={t("移出产物 ") + item.label} disabled={disabled} onClick={() => onChange(order.filter((t) => t !== item.token))}>×</button>
          </div>
        </div> : <span className="rs-empty-slot">{t("将产物放在这里")}</span>}
      </div>;
    })}</div>
  </div>;
}
